import React, { useEffect, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { ShimmerThumbnail } from "react-shimmer-effects";
import { useGetEngagementOutcomesQuery } from "../../../Data/services/engagementApi";
import { ErrorToast } from "../../../Hooks/useToast";
import { campaignLabel } from "../constants/labels";
import styles from "../styles/Dashboard.module.scss";
import { extractData, normalizeOutcomes } from "../utils";

const formatRate = (rate) => `${Number(rate || 0).toFixed(2)}%`;

const campaignHint = (slugs, fallback) => {
  const labels = (Array.isArray(slugs) ? slugs : [])
    .map((slug) => campaignLabel(slug))
    .filter(Boolean);
  return labels.length > 0 ? labels.join(" · ") : fallback;
};

export default function CampaignOutcomesSection() {
  const {
    data: outcomesRes,
    isLoading,
    isError,
    error,
  } = useGetEngagementOutcomesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (isError) {
      ErrorToast(error?.data?.message || "Failed to load campaign outcomes");
    }
  }, [isError, error]);

  const outcomes = useMemo(
    () => normalizeOutcomes(extractData(outcomesRes)),
    [outcomesRes]
  );

  const windowHours = outcomes.window_hours;
  const conversions = outcomes.conversions;
  const appReturns = outcomes.app_returns;

  return (
    <div className={`${styles.panel} mt-4`}>
      <div className={styles.panelHeader}>
        <h5 className={styles.panelTitle}>Campaign outcomes</h5>
        <p className={styles.panelHint}>
          {windowHours}-hour window after a successful send. Only mature sends
          (sent at least {windowHours} hours ago). Attribution is per send.
        </p>
        <p className={styles.panelHint}>
          App-return history is only accurate after session logging shipped.
        </p>
      </div>

      {isLoading ? (
        <Row className={`${styles.matchRow} w-100 g-3`}>
          {Array.from({ length: 2 }).map((_, i) => (
            <Col key={i} md={6}>
              <ShimmerThumbnail height={128} rounded />
            </Col>
          ))}
        </Row>
      ) : (
        <Row className={`${styles.matchRow} w-100 g-3`}>
          <Col md={6}>
            <div className={styles.outcomeTile}>
              <p className={styles.outcomeLabel}>Conversion</p>
              <p className={styles.outcomeRate}>
                {formatRate(conversions.rate)}
              </p>
              <p className={styles.outcomeDetail}>
                {conversions.converted} converted of {conversions.sent} sent
              </p>
              <p className={styles.outcomeHint}>
                {campaignHint(conversions.campaigns, "Free user upsell")}
              </p>
            </div>
          </Col>
          <Col md={6}>
            <div className={styles.outcomeTile}>
              <p className={styles.outcomeLabel}>App return</p>
              <p className={styles.outcomeRate}>
                {formatRate(appReturns.rate)}
              </p>
              <p className={styles.outcomeDetail}>
                {appReturns.returned} returned of {appReturns.sent} sent
              </p>
              <p className={styles.outcomeHint}>
                {campaignHint(appReturns.campaigns, "Inactive 7 / 14 / 30 days")}
              </p>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
}
